import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Empty, Row, Table } from "antd";
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { analyticsApi } from "../../../services/api";
import { ChartState, MetricLabel, Shimmer } from "./ChartParts";
import DeltaBadge from "./DeltaBadge";
import {
  ModuleMoversCard, NewReturningCard, TenantAdoptionCard, UsageDepthCard, WebAppShareCard, WeekdayCard,
} from "./CompareInsights";
import {
  delta, formatRange, tenantAdoption, topMovers, webAppSplit, weekdayAlignTrend, weekdayAverages, zipBy,
} from "../gaCompare";
import {
  COMPARE_COLORS, LEGEND_STYLE, MAX_CONCURRENT_REQUESTS, METRIC_HINTS, createRequestPool, fmtDuration, fmtNumber, fmtPct,
  xLabel, yLabel,
} from "../gaFormat";

const {
  current: CURRENT_COLOR, previous: PREVIOUS_COLOR, previousBar: PREVIOUS_BAR_COLOR, up: UP_COLOR, down: DOWN_COLOR,
} = COMPARE_COLORS;
const MOVERS_PER_SIDE = 10;

// Each block fetches the same endpoint twice — once per period — with identical filters.
const COMPARE_FETCHERS = {
  overview: { fetch: (p) => analyticsApi.getOverview(p) },
  usersOverTime: { fetch: (p) => analyticsApi.getUsersOverTime(p) },
  deviceBreakdown: { fetch: (p) => analyticsApi.getDeviceBreakdown(p) },
  clientBreakdown: { fetch: (p) => analyticsApi.getClientBreakdown({ ...p, limit: 100 }) },
  moduleGroups: { fetch: (p) => analyticsApi.getModuleGroupBreakdown(p) },
  // Both sides fetched deep so module movers see modules that dropped out of either top list.
  topModules: { fetch: (p) => analyticsApi.getTopModules({ ...p, limit: 200 }) },
};

const KPIS = [
  { key: "totalUsers", label: "Total Users", hint: METRIC_HINTS.totalUsers, format: fmtNumber },
  { key: "activeUsers", label: "Active Users", hint: METRIC_HINTS.activeUsers, format: fmtNumber },
  { key: "newUsers", label: "New Users", hint: METRIC_HINTS.newUsers, format: fmtNumber },
  { key: "sessions", label: "Sessions", hint: METRIC_HINTS.sessions, format: fmtNumber },
  { key: "screenPageViews", label: "Views", hint: METRIC_HINTS.views, format: fmtNumber },
  { key: "engagementRate", label: "Engagement", hint: METRIC_HINTS.engagementRate, format: fmtPct, mode: "points" },
  { key: "bounceRate", label: "Bounce", hint: METRIC_HINTS.bounceRate, format: fmtPct, mode: "points", invert: true },
  { key: "averageSessionDuration", label: "Avg Session", hint: METRIC_HINTS.avgSessionDuration, format: fmtDuration },
];

const TREND_METRICS = [
  {
    key: "activeUsers",
    title: "Active users",
    totalKey: "activeUsers",
    hint: `${METRIC_HINTS.activeUsers} Plotted per day from the daily active-users report. The comparison period above (KPIs, totals) is the exact previous period with no gap — but on this chart, a few days are trimmed from each end so Day 1 of both lines is the same weekday: the oldest days of the comparison line and the newest days of the current line, however many it takes to land on a matching weekday (0 for a range that's already a multiple of 7 days, e.g. 7 or 28). Nothing above the chart is affected by this trim.`,
  },
  {
    key: "sessions",
    title: "Sessions",
    totalKey: "sessions",
    hint: `${METRIC_HINTS.sessions} Summed per day, weekday-aligned the same way as Active users (see its tooltip) — a few of the oldest comparison days and newest current days are trimmed from this chart only, not from the totals above.`,
  },
  {
    key: "views",
    title: "Views",
    totalKey: "screenPageViews",
    hint: `${METRIC_HINTS.views} Summed per day, weekday-aligned the same way as Active users (see its tooltip) — a few of the oldest comparison days and newest current days are trimmed from this chart only, not from the totals above.`,
  },
];

function datesOf([start, end]) {
  const out = [];
  for (let d = start.startOf("day"); !d.isAfter(end, "day"); d = d.add(1, "day")) out.push(d);
  return out;
}

function pctText(current, previous) {
  const { pct, isNew } = delta(current, previous);
  if (isNew) return "new";
  if (pct == null) return "";
  return `${pct > 0 ? "+" : ""}${(pct * 100).toFixed(0)}%`;
}

function groupedBars(data, key, metric) {
  return zipBy(key, data?.current, data?.previous).map(({ key: k, current, previous }) => ({
    name: k,
    current: current?.[metric] || 0,
    previous: previous?.[metric] || 0,
    _label: pctText(current?.[metric] || 0, previous?.[metric] || 0),
  }));
}

// params / compareParams: identical filter params, differing only in startDate/endDate.
export default function ComparePeriods({ params, compareParams, currentRange, compareRange, onAuthError }) {
  const [blocks, setBlocks] = useState(() =>
    Object.fromEntries(Object.keys(COMPARE_FETCHERS).map((k) => [k, { loading: true, error: null, data: null }]))
  );
  const requestSeq = useRef(0);

  const currentLabel = formatRange(currentRange);
  const previousLabel = formatRange(compareRange);

  const fetchBlock = useCallback((key, seq, run = (fn) => fn()) => {
    const { fetch, fetchPrevious = fetch } = COMPARE_FETCHERS[key];
    setBlocks((prev) => ({ ...prev, [key]: { ...prev[key], loading: true, error: null } }));
    Promise.all([run(() => fetch(params)), run(() => fetchPrevious(compareParams))])
      .then(([cur, prev]) => {
        if (seq !== requestSeq.current) return;
        setBlocks((b) => ({ ...b, [key]: { loading: false, error: null, data: { current: cur.data, previous: prev.data } } }));
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        onAuthError?.(err?.response?.status);
        const msg = err?.response?.data?.error || err?.message || "Request failed";
        setBlocks((b) => ({ ...b, [key]: { ...b[key], loading: false, error: msg } }));
      });
  }, [params, compareParams, onAuthError]);

  useEffect(() => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const run = createRequestPool(MAX_CONCURRENT_REQUESTS);
    Object.keys(COMPARE_FETCHERS).forEach((key) => fetchBlock(key, seq, run));
  }, [fetchBlock]);

  const retry = (key) => fetchBlock(key, requestSeq.current);

  const overview = blocks.overview.data;

  const trendData = useMemo(() => {
    const data = blocks.usersOverTime.data;
    if (!data) return [];
    const byDate = (rows) => new Map((rows || []).map((r) => [r.date, r]));
    const cur = byDate(data.current);
    const prev = byDate(data.previous);
    // The fetched ranges (and every KPI/total elsewhere) use the exact previous period —
    // this trim is purely so the day-by-day line pairs up by weekday; see weekdayAlignTrend.
    const { current: curDates, previous: prevDates } = weekdayAlignTrend(datesOf(currentRange), datesOf(compareRange));
    return curDates.map((d, i) => {
      const c = cur.get(d.format("YYYY-MM-DD")) || {};
      const p = prevDates[i] ? prev.get(prevDates[i].format("YYYY-MM-DD")) || {} : {};
      // weekday is d's (the current period's), which — thanks to weekdayAlignTrend — is
      // always the same weekday prevDates[i] falls on too.
      const point = { day: i + 1, weekday: d.format("ddd"), curDate: d.format("D MMM"), prevDate: prevDates[i]?.format("D MMM") };
      for (const m of TREND_METRICS) {
        point[`cur_${m.key}`] = c[m.key] || 0;
        point[`prev_${m.key}`] = p[m.key] || 0;
      }
      return point;
    });
  }, [blocks.usersOverTime.data, currentRange, compareRange]);

  const weekdayByDay = useMemo(() => new Map(trendData.map((p) => [p.day, p.weekday])), [trendData]);

  const deviceData = useMemo(() => groupedBars(blocks.deviceBreakdown.data, "device", "sessions"), [blocks.deviceBreakdown.data]);
  const moduleGroupData = useMemo(() => groupedBars(blocks.moduleGroups.data, "group", "views"), [blocks.moduleGroups.data]);

  const clientRows = useMemo(() => {
    const data = blocks.clientBreakdown.data;
    if (!data) return [];
    return zipBy("clientName", data.current, data.previous).map(({ key, current, previous }) => {
      const now = current?.activeUsers || 0;
      const before = previous?.activeUsers || 0;
      return { client: key, now, before, change: now - before };
    });
  }, [blocks.clientBreakdown.data]);

  const moversData = useMemo(() => {
    const ups = clientRows.filter((r) => r.change > 0).sort((a, b) => b.change - a.change).slice(0, MOVERS_PER_SIDE);
    const downs = clientRows.filter((r) => r.change < 0).sort((a, b) => a.change - b.change).slice(0, MOVERS_PER_SIDE).reverse();
    return [...ups, ...downs];
  }, [clientRows]);

  const moduleRows = useMemo(() => {
    const data = blocks.topModules.data;
    if (!data) return [];
    return zipBy("title", data.current, data.previous).map(({ key, current, previous }) => {
      const now = current?.views || 0;
      const before = previous?.views || 0;
      return { title: key, now, before, change: now - before };
    });
  }, [blocks.topModules.data]);

  const moduleMovers = useMemo(() => topMovers(moduleRows, 8), [moduleRows]);

  const adoption = useMemo(() => tenantAdoption(clientRows), [clientRows]);

  const weekdays = useMemo(() => {
    const data = blocks.usersOverTime.data;
    return {
      current: weekdayAverages(data?.current, currentRange, "activeUsers"),
      previous: weekdayAverages(data?.previous, compareRange, "activeUsers"),
    };
  }, [blocks.usersOverTime.data, currentRange, compareRange]);

  const webApp = useMemo(() => {
    const data = blocks.deviceBreakdown.data;
    return { current: webAppSplit(data?.current), previous: webAppSplit(data?.previous) };
  }, [blocks.deviceBreakdown.data]);

  const labels = { current: currentLabel, previous: previousLabel };

  const changeColumns = (nowTitle, beforeTitle) => [
    { title: nowTitle, dataIndex: "now", align: "right", width: 100, render: fmtNumber, sorter: (a, b) => a.now - b.now },
    { title: beforeTitle, dataIndex: "before", align: "right", width: 100, render: fmtNumber, sorter: (a, b) => a.before - b.before },
    {
      title: "Change",
      key: "change",
      align: "right",
      width: 110,
      defaultSortOrder: "descend",
      sorter: (a, b) => (a.now - a.before) - (b.now - b.before),
      render: (_, r) => <DeltaBadge current={r.now} previous={r.before} />,
    },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-[12px] text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-5 border-0 border-t-2 border-solid border-blue-500" /> {currentLabel}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-5 border-0 border-t-2 border-dashed border-slate-600" /> {previousLabel}
        </span>
      </div>

      <Row gutter={[8, 8]} className="mb-4">
        {KPIS.map((kpi) => (
          <Col key={kpi.key} xs={12} sm={8} md={6} lg={3} className="flex">
            <Card size="small" className="flex-1 text-center" styles={{ body: { padding: "8px 10px" } }}>
              <div className="text-[11px] text-slate-500 mb-1">
                <MetricLabel title={kpi.label} hint={kpi.hint} />
              </div>
              {blocks.overview.loading ? (
                <Shimmer className="h-[34px] w-16 mx-auto" />
              ) : blocks.overview.error ? (
                <Button type="link" size="small" onClick={() => retry("overview")}>retry</Button>
              ) : (
                <>
                  <div className="text-[18px] font-semibold text-slate-900 leading-[1.2]">{kpi.format(overview?.current?.[kpi.key])}</div>
                  <div className="mt-0.5">
                    <DeltaBadge current={overview?.current?.[kpi.key]} previous={overview?.previous?.[kpi.key]} mode={kpi.mode} invert={kpi.invert} />
                  </div>
                  <div className="text-[11px] text-slate-400 whitespace-nowrap">prev {kpi.format(overview?.previous?.[kpi.key])}</div>
                </>
              )}
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} className="items-stretch">
        <Col xs={24} xl={12} className="flex flex-col">
          <TenantAdoptionCard state={blocks.clientBreakdown} onRetry={() => retry("clientBreakdown")} adoption={adoption} />
        </Col>
        <Col xs={24} xl={12} className="flex flex-col">
          <UsageDepthCard state={blocks.overview} onRetry={() => retry("overview")} overview={overview} />
        </Col>

        {TREND_METRICS.map((m) => (
          <Col key={m.key} xs={24} lg={8} className="flex flex-col">
            <Card
              size="small"
              className="flex-1"
              title={<MetricLabel title={`${m.title} over time`} hint={m.hint} />}
              extra={overview && <DeltaBadge current={overview.current?.[m.totalKey]} previous={overview.previous?.[m.totalKey]} />}
            >
              <ChartState state={blocks.usersOverTime} skeleton="line" height={220} onRetry={() => retry("usersOverTime")} empty={trendData.length === 0}>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trendData} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => weekdayByDay.get(d) || `Day ${d}`} minTickGap={24} />
                    <YAxis tick={{ fontSize: 10 }} width={44} />
                    <Tooltip
                      labelFormatter={(_, payload) => {
                        const p = payload?.[0]?.payload;
                        return p ? `Day ${p.day}, ${p.weekday}: ${p.curDate} vs ${p.prevDate}` : "";
                      }}
                      formatter={(v) => Number(v).toLocaleString()}
                    />
                    <Line type="monotone" dataKey={`cur_${m.key}`} name={currentLabel} stroke={CURRENT_COLOR} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey={`prev_${m.key}`} name={previousLabel} stroke={PREVIOUS_COLOR} strokeWidth={2} strokeDasharray="5 4" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartState>
            </Card>
          </Col>
        ))}

        <Col xs={24} lg={12} className="flex flex-col">
          <WeekdayCard state={blocks.usersOverTime} onRetry={() => retry("usersOverTime")} current={weekdays.current} previous={weekdays.previous} labels={labels} />
        </Col>
        <Col xs={24} lg={12} className="flex flex-col">
          <WebAppShareCard state={blocks.deviceBreakdown} onRetry={() => retry("deviceBreakdown")} current={webApp.current} previous={webApp.previous} labels={labels} />
        </Col>

        <Col xs={24} lg={12} className="flex flex-col">
          <Card
            size="small"
            className="flex-1"
            title={
              <MetricLabel
                title="Sessions by platform & device"
                hint="Sessions grouped by deviceCategory: Desktop, Tablet and Mobile are the web app in a browser; iOS and Android are the native apps. Current and previous period side by side, with the % change over each bar."
              />
            }
          >
            <ChartState state={blocks.deviceBreakdown} skeleton="bar" onRetry={() => retry("deviceBreakdown")} empty={deviceData.length === 0}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={deviceData} margin={{ top: 24, right: 16, bottom: 24, left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} height={52} label={xLabel("Platform / device")} />
                  <YAxis tick={{ fontSize: 11 }} label={yLabel("Sessions")} />
                  <Tooltip formatter={(v) => Number(v).toLocaleString()} />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                  <Bar dataKey="current" name={currentLabel} fill={CURRENT_COLOR} radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="_label" position="top" style={{ fontSize: 10, fill: "#475569" }} />
                  </Bar>
                  <Bar dataKey="previous" name={previousLabel} fill={PREVIOUS_BAR_COLOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartState>
          </Card>
        </Col>

        <Col xs={24} lg={12} className="flex flex-col">
          <Card
            size="small"
            className="flex-1"
            title={
              <MetricLabel
                title="Views by module group"
                hint="Total page views (web) and screen views (app) per module group, current vs previous period. Web pages and app screens are each classified into the same group set from their URL path or screen name."
              />
            }
          >
            <ChartState state={blocks.moduleGroups} skeleton="bar" onRetry={() => retry("moduleGroups")} empty={moduleGroupData.length === 0}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={moduleGroupData} margin={{ top: 24, right: 16, bottom: 28, left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 11 }} label={yLabel("Views")} />
                  <Tooltip formatter={(v) => Number(v).toLocaleString()} />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                  <Bar dataKey="current" name={currentLabel} fill={CURRENT_COLOR} radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="_label" position="top" style={{ fontSize: 9, fill: "#475569" }} />
                  </Bar>
                  <Bar dataKey="previous" name={previousLabel} fill={PREVIOUS_BAR_COLOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartState>
          </Card>
        </Col>

        <Col xs={24}>
          <NewReturningCard state={blocks.overview} onRetry={() => retry("overview")} overview={overview} labels={labels} />
        </Col>

        <Col xs={24}>
          <Card
            size="small"
            title={
              <MetricLabel
                title="Biggest movers by client"
                hint="Change in active users per tenant between the two periods. Top 10 increases and top 10 decreases; the table lists every tenant seen in either period."
              />
            }
          >
            <ChartState state={blocks.clientBreakdown} skeleton="bar" height={320} onRetry={() => retry("clientBreakdown")} empty={clientRows.length === 0}>
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  {moversData.length === 0 ? (
                    <Empty description="No change between periods" />
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(160, moversData.length * 26 + 40)}>
                      <BarChart data={moversData} layout="vertical" margin={{ top: 8, right: 40, bottom: 8, left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis type="category" dataKey="client" tick={{ fontSize: 11 }} width={120} />
                        <Tooltip
                          formatter={(v, _n, item) => [`${v > 0 ? "+" : ""}${Number(v).toLocaleString()} (${fmtNumber(item.payload.before)} → ${fmtNumber(item.payload.now)})`, "Active users"]}
                        />
                        <Bar dataKey="change" name="Change in active users" radius={[0, 4, 4, 0]}>
                          {moversData.map((r) => (
                            <Cell key={r.client} fill={r.change > 0 ? UP_COLOR : DOWN_COLOR} />
                          ))}
                          <LabelList dataKey="change" position="right" style={{ fontSize: 10, fill: "#475569" }} formatter={(v) => (v > 0 ? `+${v}` : v)} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Col>
                <Col xs={24} lg={12}>
                  <Table
                    rowKey="client"
                    size="small"
                    dataSource={clientRows}
                    pagination={{ pageSize: 10, showSizeChanger: false, size: "small" }}
                    columns={[
                      { title: "Client", dataIndex: "client", ellipsis: true, sorter: (a, b) => a.client.localeCompare(b.client) },
                      ...changeColumns("Users now", "Before"),
                    ]}
                  />
                </Col>
              </Row>
            </ChartState>
          </Card>
        </Col>

        <Col xs={24}>
          <ModuleMoversCard state={blocks.topModules} onRetry={() => retry("topModules")} movers={moduleMovers} />
        </Col>
      </Row>
    </div>
  );
}
